// components/errors/ServerErrorPage.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ServerCrash, RefreshCw, Home, Mail, AlertTriangle, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GlassCard, GlassButton, GlassAlert } from '../ui/GlassComponents';

const ServerErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('errors');
  const { t: tCommon } = useTranslation('common');

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Animated background gradient */}
      <div className="fixed inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 via-transparent to-orange-900/10 animate-pulse" />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 30% 50%, rgba(239, 68, 68, 0.1) 0%, transparent 50%)`,
          }}
        />
      </div>

      <div className="relative z-10 max-w-2xl w-full space-y-6">
        {/* Main 500 error card */}
        <GlassCard className="backdrop-blur-xl">
          <div className="text-center space-y-6 p-8">
            {/* 500 with icon */}
            <div className="relative mx-auto">
              <div className="text-8xl font-bold text-white/10 select-none">500</div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-red-500/10 backdrop-blur-sm border border-red-500/30 rounded-full p-6 animate-pulse">
                  <ServerCrash className="h-12 w-12 text-red-400" />
                </div>
              </div>
            </div>

            {/* Error title */}
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {t('server.title')}
              </h1>
              <p className="text-lg text-white/60">
                {t('server.subtitle')}
              </p>
            </div>

            {/* Error message */}
            <GlassAlert variant="danger" className="text-start">
              <div className="space-y-2">
                <p className="text-white/90 font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {t('server.alert_title')}
                </p>
                <p className="text-white/70 text-sm">
                  {t('server.description')}
                </p>
              </div>
            </GlassAlert>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <GlassButton
                variant="primary"
                size="lg"
                onClick={handleRefresh}
                className="group"
              >
                <RefreshCw className="h-5 w-5 me-2 group-hover:rotate-180 transition-transform duration-500" />
                {t('server.retry_now')}
              </GlassButton>

              <GlassButton
                variant="secondary"
                size="lg"
                onClick={() => navigate('/')}
                className="group"
              >
                <Home className="h-5 w-5 me-2 group-hover:scale-110 transition-transform" />
                {tCommon('actions.home')}
              </GlassButton>
            </div>

            {/* Contact support */}
            <div className="pt-4 border-t border-white/10">
              <button
                onClick={() => window.location.href = 'mailto:support@gestion-officiels.dz'}
                className="flex items-center justify-center gap-2 text-white/50 hover:text-white/70 transition-colors text-sm mx-auto"
              >
                <Mail className="h-4 w-4" />
                <span>{t('server.contact_support')}</span>
              </button>
            </div>
          </div>
        </GlassCard>

        {/* Status info card */}
        <GlassCard className="backdrop-blur-md">
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white/90 font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-400" />
                {t('server.system_status_title')}
              </h3>
              <span className="px-2 py-1 rounded-full bg-red-500/20 text-red-400 text-xs font-medium animate-pulse">
                {t('server.incident_badge')}
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                <span className="text-white/70 text-sm">{t('server.db_label')}</span>
                <span className="flex items-center gap-2 text-red-400 text-sm">
                  <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                  {t('server.db_status')}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                <span className="text-white/70 text-sm">{t('server.api_label')}</span>
                <span className="flex items-center gap-2 text-amber-400 text-sm">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  {t('server.api_status')}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                <span className="text-white/70 text-sm">{t('server.notifications_label')}</span>
                <span className="flex items-center gap-2 text-emerald-400 text-sm">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  {t('server.notifications_status')}
                </span>
              </div>
            </div>

            <p className="text-white/50 text-xs text-center pt-2">
              {t('server.last_update')}
            </p>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default ServerErrorPage;
